import { getAggregatedSymbols, getMemberFirmsRevenue, getRevenueAnalytics } from './http/labRevenueAnalytics';
import type {
    GetAggregatedSymbolsOptions,
    GetMemberFirmsRevenueOptions,
    GetRevenueAnalyticsOptions,
} from './http/labRevenueAnalytics';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type {
    AggregatedSymbolsRawRow,
    MemberFirmsRawRow,
    RevenueAnalyticsRawRow,
} from 'src/admin/LAB/RevenueAnalytics/types';

/** QueryKey constant for provider revenue analytics. */
export const LAB_REVENUE_ANALYTICS = 'lab-revenue-analytics';

/** QueryKey constant for the per-symbol aggregation. */
export const LAB_AGGREGATED_SYMBOLS = 'lab-aggregated-symbols';

/** QueryKey constant for the per-firm aggregation. */
export const LAB_MEMBER_FIRMS_REVENUE = 'lab-member-firms-revenue';

// Revenue is live data: the shared client's day-long default would serve the
// first load from cache whenever the page is revisited.
const STALE_TIME_MS = 0;

/** Provider segment of the key: order-insensitive over the ids, `null` for the aggregate. */
export const revenueAnalyticsProviderKey = (providerIds: ReadonlyArray<string> = []): string | null =>
    providerIds.length === 0 ? null : [...providerIds].sort().join(',');

/**
 * Query for one page of provider revenue analytics. With no
 * `providerIds` it loads the aggregate across all providers; with some it
 * narrows to those providers.
 *
 * `providerIds`, `firmId`, `memberFirmId`, `symbol`, `filter`, `orderby`, `skip`, and `top` are all
 * part of the key so distinct provider / firm / filter / sort / page / search slices don't
 * clobber each other, and one firm's slices can be invalidated together via the
 * `[LAB_REVENUE_ANALYTICS, revenueAnalyticsProviderKey(providerIds), firmId ?? null]` prefix. Toggling superuser needs
 * no key dimension: QueryCacheUserService resets user-scoped queries when
 * the effective identity changes. Callers own the `enabled` gate (the
 * scope must be known before the query runs).
 */
export const getRevenueAnalyticsQuery = (
    options: GetRevenueAnalyticsOptions = {},
): QueryObserverOptions<RevenueAnalyticsRawRow[], Error, RevenueAnalyticsRawRow[]> => {
    const { providerIds, firmId, memberFirmId, filter, skip, top, orderby, symbol } = options;
    return {
        queryKey: [
            LAB_REVENUE_ANALYTICS,
            revenueAnalyticsProviderKey(providerIds),
            firmId ?? null,
            memberFirmId ?? null,
            symbol ?? null,
            filter ?? null,
            orderby ?? null,
            skip ?? null,
            top ?? null,
        ],
        queryFn: () => getRevenueAnalytics(options),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Query for one page of the per-symbol aggregation backing the Aggregated
 * Symbols tab. Keyed like {@link getRevenueAnalyticsQuery} (minus the
 * provider dimension, which does not apply to symbol rows); one firm's
 * slices are invalidated together via the
 * `[LAB_AGGREGATED_SYMBOLS, firmId ?? null]` prefix. Callers own the
 * `enabled` gate.
 */
export const getAggregatedSymbolsQuery = (
    options: GetAggregatedSymbolsOptions = {},
): QueryObserverOptions<AggregatedSymbolsRawRow[], Error, AggregatedSymbolsRawRow[]> => {
    const { firmId, memberFirmId, filter, skip, top, orderby, symbol } = options;
    return {
        queryKey: [
            LAB_AGGREGATED_SYMBOLS,
            firmId ?? null,
            memberFirmId ?? null,
            symbol ?? null,
            filter ?? null,
            orderby ?? null,
            skip ?? null,
            top ?? null,
        ],
        queryFn: () => getAggregatedSymbols(options),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Query for one page of the per-firm aggregation backing the Member Firms
 * tab. Keyed like {@link getRevenueAnalyticsQuery} (minus the symbol
 * search, which the firm rows do not carry); one firm's slices are
 * invalidated together via the
 * `[LAB_MEMBER_FIRMS_REVENUE, revenueAnalyticsProviderKey(providerIds), firmId ?? null]`
 * prefix. Callers own the `enabled` gate.
 */
export const getMemberFirmsRevenueQuery = (
    options: GetMemberFirmsRevenueOptions = {},
): QueryObserverOptions<MemberFirmsRawRow[], Error, MemberFirmsRawRow[]> => {
    const { providerIds, firmId, memberFirmId, filter, skip, top, orderby } = options;
    return {
        queryKey: [
            LAB_MEMBER_FIRMS_REVENUE,
            revenueAnalyticsProviderKey(providerIds),
            firmId ?? null,
            memberFirmId ?? null,
            filter ?? null,
            orderby ?? null,
            skip ?? null,
            top ?? null,
        ],
        queryFn: () => getMemberFirmsRevenue(options),
        staleTime: STALE_TIME_MS,
    };
};
